import { useCallback, useEffect, useState } from "react";
import { api, API, formatErr } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { PAY_STATUS, fmtD, fmtDT, tl } from "@/lib/subscription";

const openReceipt = async (pid) => {
  try {
    const r = await fetch(`${API}/sub-payments/${pid}/receipt`, { credentials: "include" });
    if (!r.ok) throw new Error();
    window.open(URL.createObjectURL(await r.blob()), "_blank", "noopener");
  } catch { toast.error("Dekont açılamadı"); }
};

const Decide = ({ p, onDone }) => {
  const [ok, setOk] = useState(false);
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const call = async (url, body, msg) => { try { await api.post(url, body); toast.success(msg); onDone(); } catch (e) { toast.error(formatErr(e.response?.data?.detail)); } };
  return (
    <div className="mt-2 grid gap-2 sm:grid-cols-2">
      <div className="rounded-lg bg-[hsl(var(--success))]/10 p-2 space-y-2">
        <label className="flex items-start gap-2 text-xs"><Checkbox checked={ok} onCheckedChange={(v) => setOk(v === true)} data-testid={`sub-pay-verify-${p.id}`} />
          {tl(p.amount_tl)} tutarının {p.bank_snapshot?.bank_name} hesabına ulaştığını banka hareketlerinden doğruladım.</label>
        <Input placeholder="Not (ops.)" className="h-8" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`sub-pay-note-${p.id}`} />
        <Button size="sm" disabled={!ok} onClick={() => window.confirm(`${p.duration_days} günlük abonelik tanımlansın mı?`) && call(`/admin/sub-payments/${p.id}/approve`, { verified: true, note }, "Ödeme onaylandı, abonelik aktif")} data-testid={`sub-pay-approve-${p.id}`}>Onayla ve aktifleştir</Button>
      </div>
      <div className="rounded-lg bg-destructive/10 p-2 space-y-2">
        <Input placeholder="Ret nedeni (zorunlu, işletmeye gösterilir)" className="h-8" value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`sub-pay-reason-${p.id}`} />
        <Button size="sm" variant="destructive" disabled={reason.trim().length < 3} onClick={() => call(`/admin/sub-payments/${p.id}/reject`, { reason }, "Ödeme reddedildi")} data-testid={`sub-pay-reject-${p.id}`}>Reddet</Button>
      </div>
    </div>
  );
};

export const SubPayments = ({ onChanged }) => {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState(null);
  const load = useCallback(() => { api.get("/admin/sub-payments", { params: status === "all" ? {} : { status } }).then(({ data }) => setRows(data)).catch(() => setRows([])); }, [status]);
  useEffect(() => { load(); }, [load]);
  const done = () => { load(); onChanged(); };
  return (
    <div className="space-y-3" data-testid="sub-admin-payments">
      <Select value={status} onValueChange={setStatus}>
        <SelectTrigger className="w-56" data-testid="sub-pay-filter"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="pending">Onay bekleyenler</SelectItem><SelectItem value="approved">Onaylananlar</SelectItem>
          <SelectItem value="rejected">Reddedilenler</SelectItem><SelectItem value="cancelled">İptal edilenler</SelectItem><SelectItem value="all">Tümü</SelectItem></SelectContent>
      </Select>
      {rows?.length === 0 && <div className="text-sm text-muted-foreground">Kayıt yok.</div>}
      {rows?.map((p) => (
        <div key={p.id} className="rounded-2xl border p-3 text-sm" data-testid={`sub-admin-payment-${p.id}`}>
          <div className="flex justify-between flex-wrap gap-2">
            <div><b>{p.provider_email}</b> · {p.package_name} ({p.duration_days} gün) · <b>{tl(p.amount_tl)}</b></div>
            <Badge className={PAY_STATUS[p.status][1]}>{PAY_STATUS[p.status][0]}</Badge>
          </div>
          <div className="text-xs text-muted-foreground mt-1">Bildirim {fmtDT(p.created_at)} · Havale {fmtD(p.transfer_date)} · Gönderen: {p.payer_name} · {p.bank_snapshot?.bank_name} {p.bank_snapshot?.iban}{p.reference ? ` · Ref: ${p.reference}` : ""}</div>
          {p.note && <div className="text-xs italic">Not: {p.note}</div>}
          {p.reject_reason && <div className="text-xs text-destructive">Ret: {p.reject_reason}</div>}
          {p.status === "approved" && <div className="text-xs">Doğrulandı {fmtDT(p.decided_at)} · Yeni bitiş {fmtD(p.paid_until_after)}{p.admin_note ? ` · ${p.admin_note}` : ""}</div>}
          <div className="mt-1">{p.receipt_path ? <Button size="sm" variant="outline" onClick={() => openReceipt(p.id)} data-testid={`sub-admin-receipt-${p.id}`}>Dekontu aç</Button> : <span className="text-xs text-muted-foreground">Dekont eklenmemiş</span>}</div>
          {p.status === "pending" && <Decide p={p} onDone={done} />}
        </div>
      ))}
    </div>
  );
};
